import { Box } from '@mui/material'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import SaveRecipeDialog from '@/components/SaveRecipeDialog'
import WorkFlowTab from '@/components/WorkFlowTab'
import NewKonvaCanvas from '@/components/canvas/NewKonvaCanvas'
import ImageEdit from '@/components/imageEdit/ImageEdit'
import { useAppSelector } from '@/hooks/redux'
import { useCanvasSize } from '@/hooks/useCanvasSize'
import useGenerateImage from '@/hooks/useGenerateImage'
import CaseInfoLayout from '@/layout/CaseInfoLayout'
import {
  useGenerateEnamelRecipeMutation,
  useSavePdfMutation,
  useSaveRecipeMutation,
} from '@/store/apiService'
import type { LABColor } from '@/store/appSlice'
import {
  setEnamelPosition,
  setEnamelRecipeOutput,
  setMeasuringRegion,
} from '@/store/pfmCrown'
import {
  ImageCalibrationStatus,
  setImageCalibrationStatus,
} from '@/store/sessionSlice'
import { Workflows } from '@/utils/constants'
import {
  getDehazedImageUrl,
  hasNonEmptyOutputSingleCanvas,
  saveRecipeGeneric,
} from '@/utils/helper'

import { imageType } from '../../allInOne/utils/constants'
import EnamelPanel from './panel/EnamelPanel'

function Enamel() {
  const { t } = useTranslation('translation', { keyPrefix: 'enamel' })
  const { t: translation } = useTranslation()

  const canvasImage = useAppSelector((state) => state.app.canvasImage)
  const { containerRef, dimensions } = useCanvasSize()
  const dispatch = useDispatch()
  const combinedRef = useRef<HTMLDivElement>(null)

  const { generateImage } = useGenerateImage()
  const location = useLocation()
  const caseId = new URLSearchParams(location.search).get('caseId') || ''

  const [saveRecipedialogOpen, setSaveRecipedialogOpen] = useState(false)
  const [recipeName, setRecipeName] = useState('')

  const imageCalibrationStatus = useAppSelector(
    (state) => state.session.imageCalibrationStatus,
  )

  const [generateEnamelRecipe] = useGenerateEnamelRecipeMutation()
  const [saveRecipe, { isLoading }] = useSaveRecipeMutation()
  const [savePdf] = useSavePdfMutation()

  const enamelCeramicBrand = useAppSelector(
    (state) => state.pfmCrown.enamelCeramicBrand,
  )

  const enamelTypesByRegion = useAppSelector(
    (state) => state.pfmCrown.enamelTypesByRegion,
  )

  const measuringRegion = useAppSelector(
    (state) => state.pfmCrown.measuringRegion,
  )

  const enamelRecipe = useAppSelector((state) => state.pfmCrown.enamelRecipe)
  const path = useLocation().pathname
  const imageEdit = useAppSelector(
    (state) => state.app.imageEdit[path] || false,
  )

  const onImageClick = (
    _type: string,
    _placement: string,
    imagePos: { x: number; y: number },
    _hiddenCanvasRef: any,
    labColor: LABColor,
  ) => {
    if (
      canvasImage?.id &&
      imageCalibrationStatus?.[canvasImage?.id] ===
        ImageCalibrationStatus.CALIBRATING &&
      _type === imageType.RAW
    ) {
      if (canvasImage?.id) {
        dispatch(
          setImageCalibrationStatus({
            id: canvasImage?.id,
            status: ImageCalibrationStatus.CALIBRATED,
          }),
        )
      }
      return
    }

    if (measuringRegion) {
      dispatch(
        setEnamelPosition({
          imageId: canvasImage?.id,
          position: imagePos,
          output: {
            [measuringRegion]: measuringRegion,
          },
        }),
      )

      const targetcolor: [number, number, number] = [
        Number(labColor.L),
        Number(labColor.a),
        Number(labColor.b),
      ]

      const enamelType = enamelTypesByRegion?.[measuringRegion]

      toast.info(translation('recipeGenerate.inProgress'))

      generateEnamelRecipe({
        caseId,
        data: {
          brand: enamelCeramicBrand,
          targetcolor,
          skinenameltype: enamelType,
        },
      })
        .unwrap()
        .then((res) => {
          toast.success(t('recipeGenerated'))
          dispatch(
            setEnamelRecipeOutput({
              imageId: canvasImage?.id,
              key: measuringRegion,
              result: res,
            }),
          )
        })
        .catch((error) => {
          toast.error(error?.data?.message || t('recipeGenerationFailed'))
        })

      dispatch(setMeasuringRegion(''))
    }
  }

  const onSaveAsImage = () => {
    generateImage(
      combinedRef as React.RefObject<HTMLDivElement>,
      'Enamel-Recipe.png',
    )
  }

  const handleOpenSaveDialog = () => {
    setSaveRecipedialogOpen(true)
  }

  const handleSaveToProject = async (targetCaseId: string) => {
    await saveRecipeGeneric({
      caseId: targetCaseId,
      canvasImage,
      recipeName,
      output: enamelRecipe[canvasImage?.id],
      workflow: Workflows?.PFM_CROWN,
      mutationFn: saveRecipe,
      closeDialog: () => setSaveRecipedialogOpen(false),
      recipeType: 'enamel',
    })
  }

  const onSaveAsPDF = async () => {
    await saveRecipeGeneric({
      caseId,
      canvasImage,
      recipeName,
      output: enamelRecipe[canvasImage?.id],
      workflow: Workflows?.PFM_CROWN,
      mutationFn: savePdf,
      recipeType: 'enamel',
      isPdf: true,
    })
  }
  return (
    <CaseInfoLayout
      rightPanel={
        <EnamelPanel
          containerRef={containerRef as React.RefObject<HTMLDivElement>}
        />
      }
      disabled={hasNonEmptyOutputSingleCanvas(enamelRecipe, canvasImage?.id)}
      onSaveAsImage={onSaveAsImage}
      onSaveAsPDF={onSaveAsPDF}
      handleSaveToProject={handleOpenSaveDialog}
    >
      <Box display="flex" flexDirection="column" flexGrow={1}>
        <Box
          ref={containerRef}
          display="flex"
          flexGrow={1}
          flexDirection={'column'}
        >
          <WorkFlowTab width={dimensions.width} />
          {!imageEdit ? (
            <>
              <NewKonvaCanvas
                type={canvasImage?.type}
                imageUrl={
                  getDehazedImageUrl(canvasImage) ||
                  canvasImage?.latest_image ||
                  canvasImage?.calibrated_image ||
                  canvasImage?.image
                }
                width={dimensions.width}
                height={dimensions.height}
                onImageClick={onImageClick}
                imageId={canvasImage?.id}
                combinedRef={combinedRef}
                rawWidth={canvasImage?.raw_width}
                rawHeight={canvasImage?.raw_height}
                isActiveCircle={!!measuringRegion}
              />
            </>
          ) : (
            <ImageEdit selectedImage={canvasImage} />
          )}
        </Box>
      </Box>
      <SaveRecipeDialog
        saveRecipedialogOpen={saveRecipedialogOpen}
        setSaveRecipedialogOpen={setSaveRecipedialogOpen}
        recipeName={recipeName}
        setRecipeName={setRecipeName}
        onSave={handleSaveToProject}
        isLoading={isLoading}
      />
    </CaseInfoLayout>
  )
}

export default Enamel
